import type ArrayProxy from '@ember/array/proxy';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { all, dropTask, task } from 'ember-concurrency';
import { or } from 'ember-truth-helpers';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import SecurityIdScanningEmailAlerts from 'garaje/components/security/id-scanning/email-alerts';
import SecurityIdScanningVisitorTypes from 'garaje/components/security/id-scanning/visitor-types';
import SlideToggle from 'garaje/components/slide-toggle';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type Config from 'garaje/models/config';
import type Flow from 'garaje/models/flow';
import type IdScanConfiguration from 'garaje/models/id-scan-configuration';
import type IdScanPage from 'garaje/models/id-scan-page';
import type Location from 'garaje/models/location';
import type LocationRoleModel from 'garaje/models/location-role';
import type Subscription from 'garaje/models/subscription';
import type User from 'garaje/models/user';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import RSVP from 'rsvp';

interface SecurityIdScanningArgs {
  Args: {
    location: Location;
    onUpdate: (changes: { hasChanges: boolean }) => void;
    onUpgrade: () => void;
    subscription: Subscription;
    unchanged: boolean;
    searchUsers: (options: { term: string }) => Promise<LocationRoleModel[]>;
    isOpen: boolean;
    idScanConfiguration: IdScanConfiguration;
  };
}

export default class SecurityIdScanning extends Component<SecurityIdScanningArgs> {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  @tracked isOpen = false;
  @tracked hasDirtyIdScanContacts = false;
  @tracked showUnsavedEditsWarning = false;
  @tracked visitorTypes: Flow[];

  // Unlike visitorTypes, also includes flows hidden from iPad, so disabling
  // ID scanning clears their idScanPage too instead of leaving it stranded on.
  allFlows: Flow[];

  constructor(...args: ConstructorParameters<typeof Component<SecurityIdScanningArgs>>) {
    super(...args);
    this.visitorTypes = [];
    this.allFlows = [];
    this.isOpen = this.args.isOpen ?? false;
    void this.loadVisitorTypes.perform();
  }

  onEdit = (): void => {
    this.isOpen = true;
    void this.args.location.pinIdScanContacts();
  };

  onEnable = async (): Promise<void> => {
    const idScanPages = await this.nonGlobalIdScanPages();
    idScanPages.forEach((isp) => set(isp, 'enabled', true));
    this.args.onUpdate({ hasChanges: true });
    this.isOpen = true;
  };

  onDisable = async (): Promise<void> => {
    const idScanPages = await this.nonGlobalIdScanPages(this.allFlows);
    idScanPages.forEach((isp) => set(isp, 'enabled', false));
    void this.disableTask.perform();
    this.isOpen = false;
  };

  onUpdateContacts = (users: User[]): void => {
    // @ts-expect-error - ember data types missing function definition
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.location.idScanContacts.content.setObjects(users.slice());
    this.hasDirtyIdScanContacts = true;
    this.args.onUpdate({ hasChanges: true });
  };

  onCancel = async (): Promise<void> => {
    const config = await this.locationConfig();
    if (this.args.unchanged || !config.idScanningEnabled) {
      await this.onContinue();
    } else {
      this.showUnsavedEditsWarning = true;
    }
  };

  get hasSubscriptionToIDScanningFeature(): boolean {
    return !!this.state.features?.canAccessIdScanning;
  }

  // The per-location name-match toggle is gated behind the id-name-mismatch-screener
  // flag and only renders once the location's id-scan-configuration has loaded.
  get nameMatchToggleAvailable(): boolean {
    return this.featureFlags.isEnabled('id-name-mismatch-screener') && !!this.args.idScanConfiguration;
  }

  get nameMatchToggleDisabled(): boolean {
    return this.saveTask.isRunning || !this.abilities.can('manage configuration id-scanning');
  }

  // Staged in memory; saveTask persists it with the rest of the config.
  toggleNameMatch = (checked: boolean): void => {
    this.args.idScanConfiguration.nameMatchRequired = checked;
    this.args.onUpdate({ hasChanges: true });
  };

  get faceMatchToggleAvailable(): boolean {
    return this.featureFlags.isEnabled('id-face-match-screener') && !!this.args.idScanConfiguration;
  }

  get faceMatchToggleDisabled(): boolean {
    return this.saveTask.isRunning || !this.abilities.can('manage configuration id-scanning');
  }

  toggleFaceMatch = (checked: boolean): void => {
    this.args.idScanConfiguration.faceMatchRequired = checked;
    this.args.onUpdate({ hasChanges: true });
  };

  get faceMatchRequired(): boolean {
    return !!this.args.idScanConfiguration?.faceMatchRequired;
  }

  // Gated by the store-id-document-on-profile flag; shown only once the
  // id-scan-configuration has loaded. Mirrors the name/face-match toggles.
  get storeIdToProfileToggleAvailable(): boolean {
    if (!this.featureFlags.isEnabled('store-id-document-on-profile')) {
      return false;
    }
    return !!this.args.idScanConfiguration;
  }

  get storeIdToProfileToggleDisabled(): boolean {
    if (this.saveTask.isRunning) {
      return true;
    }
    return !this.abilities.can('manage configuration id-scanning');
  }

  // Staged in memory; saveTask persists it with the rest of the config.
  toggleStoreIdToProfile = (checked: boolean): void => {
    this.args.idScanConfiguration.storeIdentityDocumentOnProfile = checked;
    this.args.onUpdate({ hasChanges: true });
  };

  contactSupport = (): void => {
    const plan = this.args.subscription?.plan;

    if (plan) {
      this.metrics.trackEvent('Security - ID Scanning Contact Us Clicked', { plan });
    } else {
      this.metrics.trackEvent('Security - ID Scanning Contact Us Clicked');
    }

    if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
      window.Intercom('show');
    }
  };

  onContinue = async (): Promise<void> => {
    const idScanPages = await this.allIdScanPages(this.allFlows);
    idScanPages.filter((isp) => isp.hasDirtyAttributes).forEach((isp) => isp.rollbackAttributes());
    this.args.idScanConfiguration?.rollbackAttributes();

    const config = await this.locationConfig();
    const shouldDisable = (await this._hasNoEnabledVisitors()) && config.idScanningEnabled;
    if (shouldDisable) {
      await this._toggleEnabledTask(false);
    }
    if (this.hasDirtyIdScanContacts) {
      this.args.location.rollbackIdScanContacts();
    }
    this.showUnsavedEditsWarning = false;
    this.isOpen = false;
    this.args.onUpdate({ hasChanges: false });
  };

  loadVisitorTypes = dropTask(async () => {
    let visitorTypes: Flow[] | ArrayProxy<Flow> = [];

    try {
      visitorTypes = await this.state.loadFlows({ reload: false, locationId: this.args.location?.id });
    } catch (_) {
      // If user state flow fetching fails, fallback to location association
      try {
        visitorTypes = await this.args.location.flows;
      } catch (e) {
        visitorTypes = [];
        this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      }
    }

    try {
      const localFlows = visitorTypes.filter((vt) => !vt.isProtect);
      visitorTypes = localFlows.filter((vt) => vt.enabled);

      await this.allIdScanPages(localFlows);
      if (this.featureFlags.isEnabled('id-face-match-screener')) {
        await RSVP.all(visitorTypes.map((vt) => vt.photoPage));
      }

      this.allFlows = localFlows;
      this.visitorTypes = visitorTypes;

      return visitorTypes;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }

    return [];
  });

  allIdScanPages(visitorTypes = this.visitorTypes): Promise<IdScanPage[]> {
    return RSVP.all(visitorTypes.map((vt) => vt.idScanPage));
  }

  nonGlobalIdScanPages(visitorTypes = this.visitorTypes): Promise<IdScanPage[]> {
    // Global visitor types are managed from the global overview; exclude their
    // idScanPages so we never save changes to them via this location's settings.
    const localVisitorTypes = visitorTypes.filter((vt) => !vt.isGlobalChild);
    return RSVP.all(localVisitorTypes.map((vt) => vt.idScanPage));
  }

  async locationConfig(): Promise<Config> {
    return this.args.location.config;
  }

  saveTask = dropTask(async () => {
    try {
      const config = await this.locationConfig();
      const shouldDisable = await this._hasNoEnabledVisitors();
      if (shouldDisable) {
        const idScanPages = await this.nonGlobalIdScanPages(this.allFlows);
        idScanPages.forEach((isp) => set(isp, 'enabled', false));
        await this._toggleEnabledTask(false);
      } else if (!config.idScanningEnabled) {
        await this._toggleEnabledTask(true);
      }
      await this.args.location.save();
      const idScanPages = await this.nonGlobalIdScanPages(this.allFlows);
      const dirtyVisitorTypeIdScanPages = idScanPages.filter((idScanPage) => idScanPage.hasDirtyAttributes);
      if (dirtyVisitorTypeIdScanPages.length) {
        await all(dirtyVisitorTypeIdScanPages.map((idScanPage) => idScanPage.save()));
      }
      const idScanConfiguration = this.args.idScanConfiguration;
      if (idScanConfiguration && <boolean>(<unknown>idScanConfiguration.hasDirtyAttributes)) {
        await idScanConfiguration.save();
      }
      if (!shouldDisable) {
        this.flashMessages.showAndHideFlash('success', 'Saved!');
      }
      this.isOpen = false;
      this.args.onUpdate({ hasChanges: false });
    } catch (e) {
      this.isOpen = true;
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  enableTask = task(async () => {
    await this._toggleEnabledTask(true);
  });

  disableTask = task(async () => {
    const idScanPages = await this.nonGlobalIdScanPages(this.allFlows);
    const dirtyVisitorTypeIdScanPages = idScanPages.filter((idScanPage) => idScanPage.hasDirtyAttributes);
    if (dirtyVisitorTypeIdScanPages.length) {
      await all(dirtyVisitorTypeIdScanPages.map((idScanPage) => idScanPage.save()));
    }
    await this._toggleEnabledTask(false);
  });

  async _hasNoEnabledVisitors(): Promise<boolean> {
    const idScanPages = await this.allIdScanPages();
    const enabledVisitorTypes = idScanPages.filter((idScanPage) => idScanPage.enabled);
    return enabledVisitorTypes.length === 0;
  }

  async _toggleEnabledTask(enabled: boolean): Promise<void> {
    const config = await this.locationConfig();
    set(config, 'idScanningEnabled', enabled);
    if (enabled) {
      this.metrics.trackEvent('Security - ID Scanning Enable Requested');
    } else {
      this.metrics.trackEvent('Security - ID Scanning Disable Requested');
    }
    try {
      await config.save();
      const status = enabled ? 'enabled' : 'disabled';
      const message_title = `ID scanning ${status}`;
      this.flashMessages.showAndHideFlash('success', message_title);
      this.metrics.trackEvent('Viewed Flash Message', {
        type: 'success',
        message_title,
        message_codes: 'id_scan_updated',
        message_code_type: 'id_scan',
      });
    } catch (e) {
      const status = enabled ? 'enabling' : 'disabling';
      const message_title = `There was an issue ${status} ID scanning`;
      this.flashMessages.showFlash('error', message_title, parseErrorForDisplay(e));
      this.metrics.trackEvent('Viewed Flash Message', {
        type: 'error',
        message_title,
        message_codes: 'error_id_scan_update',
        message_code_type: 'id_scan',
      });
    }
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} @onCancel={{this.onCancel}} ...attributes as |sp|>
      {{#let sp.header sp.body sp.footer as |PanelHeader PanelBody PanelFooter|}}
        <PanelHeader
          @iconUri="/assets/images/features/badge.svg"
          @title="ID scanning"
          @description="Require that visitors scan a valid, government-issued ID on the kiosk to come on-site. Note: this feature uses artificial intelligence to check the ID."
          as |header|
        >
          <header.controls>
            {{#if this.hasSubscriptionToIDScanningFeature}}
              {{#if this.isOpen}}
                <button
                  type="button"
                  class="btn btn--small btn--brand"
                  data-test-id="save-idscan"
                  disabled={{or @unchanged this.saveTask.isRunning}}
                  {{on "click" this.saveTask.perform}}
                >
                  {{if this.saveTask.isRunning "Saving..." "Save"}}
                </button>
              {{else}}
                {{! @glint-expect-error }}
                {{#if @location.config.idScanningEnabled}}
                  <button
                    type="button"
                    data-test-id="idscan-settings-edit"
                    class="btn btn--small btn--brand"
                    {{on "click" this.onEdit}}
                  >
                    Edit
                  </button>
                {{else}}
                  <button
                    type="button"
                    data-test-id="idscan-settings-enable"
                    class="btn btn--small btn--brand"
                    disabled={{this.enableTask.isRunning}}
                    {{on "click" this.onEnable}}
                  >
                    {{if this.enableTask.isRunning "Enabling..." "Enable"}}
                  </button>
                {{/if}}
              {{/if}}
            {{else}}
              <button
                data-test-idscan-upgrade-button
                type="button"
                class="btn btn--small btn--brand"
                {{on "click" this.contactSupport}}
              >
                Contact us
              </button>
            {{/if}}
          </header.controls>
        </PanelHeader>
        <PanelBody as |PanelContent|>
          <PanelContent @style="inset">
            <SecurityIdScanningEmailAlerts
              @idScanContacts={{@location.idScanContacts.content}}
              @onUpdateContacts={{this.onUpdateContacts}}
              @searchUsers={{@searchUsers}}
              data-test-idscan-alerts
            />
            <SecurityIdScanningVisitorTypes
              @onUpdate={{@onUpdate}}
              @visitorTypes={{this.visitorTypes}}
              @loadVisitorTypes={{this.loadVisitorTypes}}
              @faceMatchRequired={{this.faceMatchRequired}}
              data-test-idscan-manage-by-visitor-type
            />
            {{! Per-location name-match toggle. Sibling to the EmailAlerts/VisitorTypes
             rows inside the same ID scanning card. Gated by id-name-mismatch-screener. }}
            {{#if this.nameMatchToggleAvailable}}
              <fieldset data-test-name-match-toggle-row class="mt-6 last:mb-6 flex justify-between items-start">
                <div class="flex flex-col mr-4">
                  <h4 class="text-carbon-70 mb-0">Require approval when ID doesn't match visitor name</h4>
                  <p class="text-carbon-50 mb-0">
                    When the name a visitor enters doesn't match the name on their scanned ID, their visit is held for
                    an admin to approve or deny.
                  </p>
                </div>
                <SlideToggle
                  data-test-name-match-toggle
                  @id="location-name-match-required-toggle"
                  @isDisabled={{this.nameMatchToggleDisabled}}
                  @isChecked={{@idScanConfiguration.nameMatchRequired}}
                  @onClick={{this.toggleNameMatch}}
                />
              </fieldset>
            {{/if}}
            {{#if this.faceMatchToggleAvailable}}
              <fieldset data-test-face-match-toggle-row class="mt-6 last:mb-6 flex justify-between items-start">
                <div class="flex flex-col mr-4">
                  <h4 class="text-carbon-70 mb-0">Require approval when the photo doesn't match the ID</h4>
                  <p class="text-carbon-50 mb-0">
                    When a visitor's photo doesn't match the photo on their scanned ID, their visit is held for an admin
                    to approve or deny.
                  </p>
                  <p data-test-face-match-photo-note class="text-carbon-50 text-sm mb-0 mt-1">
                    Visitor photos must be enabled on the visitor type for this to work.
                  </p>
                </div>
                <SlideToggle
                  data-test-face-match-toggle
                  @id="location-face-match-required-toggle"
                  @isDisabled={{this.faceMatchToggleDisabled}}
                  @isChecked={{@idScanConfiguration.faceMatchRequired}}
                  @onClick={{this.toggleFaceMatch}}
                />
              </fieldset>
            {{/if}}
            {{! Store-on-profile toggle — sibling to the name/face-match rows, gated by the
             store-id-document-on-profile flag. }}
            {{#if this.storeIdToProfileToggleAvailable}}
              <fieldset
                data-test-store-id-to-profile-toggle-row
                class="mt-6 last:mb-6 flex justify-between items-start"
              >
                <div class="flex flex-col mr-4">
                  <h4 class="text-carbon-70 mb-0">Save ID document details to the visitor's profile</h4>
                  <p class="text-carbon-50 mb-0">
                    When a visitor scans their ID, save the captured document details to their visitor profile so
                    reviewers can view it on the profile and on future visits.
                  </p>
                </div>
                <SlideToggle
                  data-test-store-id-to-profile-toggle
                  @id="location-store-id-to-profile-toggle"
                  @isDisabled={{this.storeIdToProfileToggleDisabled}}
                  @isChecked={{@idScanConfiguration.storeIdentityDocumentOnProfile}}
                  @onClick={{this.toggleStoreIdToProfile}}
                />
              </fieldset>
            {{/if}}
          </PanelContent>
          {{! @glint-expect-error }}
          {{#if @location.config.idScanningEnabled}}
            <PanelFooter>
              <button
                data-test-id="idscan-settings-disable"
                type="button"
                class="btn btn--small btn--outlineFill"
                disabled={{this.disableTask.isRunning}}
                {{on "click" this.onDisable}}
              >
                {{if this.disableTask.isRunning "Disabling..." "Disable"}}
              </button>
            </PanelFooter>
          {{/if}}
        </PanelBody>
      {{/let}}
    </SettingsPanel>
    {{#if this.showUnsavedEditsWarning}}
      <ConfirmDirtyTransition
        @headerMessage="You have unsaved changes"
        @bodyMessage="You will lose any unsaved changes. Are you sure you want to continue?"
        @confirm={{this.onContinue}}
        @cancel={{fn (mut this.showUnsavedEditsWarning) false}}
      />
    {{/if}}
  </template>
}
